import { EventActions } from '../actions'
import { ApiError } from '../api-error'
import { handleError } from '../handle-error'
import type { CardsSectionProps } from '../modules/checkout/cards-section'
import { Storage } from '../storage'
import type { CustomerData, EventState, EventView, OrderCalculation } from '../types'

const getErrorMessage = (state: EventState): string | undefined => {
    const { lang, cards } = state

    if (cards.notFound) {
        return lang.cards_notFound
    } else if (cards.alreadyAdded) {
        return lang.cards_alreadyAdded
    } else if (cards.notApplicable) {
        return lang.cards_notApplicable
    }
}

export const getCardsSection = (view: EventView): CardsSectionProps => {
    const { state, update } = view
    const { lang, cards } = state

    return {
        title: lang.cards_title,
        input: {
            type: 'text',
            id: 'card',
            label: lang.cards_label,
            value: cards.code,
            onChange: (value) => {
                state.cards.code = value
                state.cards.notFound = false
                state.cards.alreadyAdded = false
                state.cards.notApplicable = false
                update()
            },
            error: getErrorMessage(state),
        },
        button: {
            text: lang.cards_button,
            onClick: async () => {
                const { code } = cards

                if (!code) {
                    return
                }

                try {
                    // This will update state.calc (and other fields),
                    // so be careful when using previously destructured variables.
                    await EventActions.applyDiscount(view, code)

                    const member = getMemberFromCard(state.calc, code)

                    if (member) {
                        const { name, phone, email } = member
                        Storage.update({ name, email, phone: phone ?? '' })
                    }

                    state.cards.code = ''
                    state.cards.notFound = false
                    state.cards.alreadyAdded = false
                    state.cards.notApplicable = false
                    update()
                } catch (err) {
                    const errorCode = err instanceof ApiError ? err.code : null

                    if (errorCode === 'invalid-card') {
                        state.cards.notFound = true
                        update()
                    } else if (errorCode === 'card-already-added') {
                        state.cards.alreadyAdded = true
                        update()
                    } else if (errorCode === 'discount-not-applicable') {
                        state.cards.notApplicable = true
                        update()
                    } else {
                        handleError(view, err as Error)
                    }
                }
            },
        },
    }
}

const getMemberFromCard = (calc: OrderCalculation, code: string): CustomerData | undefined => {
    if (calc.memberCard?.number === code) {
        return calc.memberCard.member
    }

    for (const payment of calc.giftCardPayments) {
        if (payment.number === code) {
            return payment.member
        }
    }
}
